import React, { useEffect, useState } from "react";
import {
  View,
  ScrollView,
  Pressable,
  Platform,
  KeyboardAvoidingView,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import {
  Check,
  ChevronLeft,
  X,
  ArrowRight,
  Info,
  Upload,
} from "lucide-react-native";
import { useNavigation, useRoute } from "@react-navigation/native";

import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  DocumentRow,
  SoftSecondaryButton,
  VelvetPrimaryButton,
} from "@louise/moon-ui";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
  EMBER,
} from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import { DocumentFileTypeIcon } from "@src/packages/document-vault/DocumentUploadProcessingStep";
import { useMedicalHistoryQuestionMetadata } from "./MedicalHistoryQuestionContext";
import { METALLIC_PROGRESS_COLORS } from "./medicalHistoryVisualTokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

export type QuestionLayoutProps = {
  // Header
  title: string;
  subtitle?: string;
  headerAlignment?: "left" | "center";
  headerBottomSpacing?: number;
  /** Context chip shown in the navigation row. */
  sectionLabel?: string;
  helpText?: string;
  helpAccessibilityLabel?: string;
  onBack?: () => void;

  // Progress (optional section stepper — pill tabs)
  progress?: {
    sections: {
      label: string;
      isActive: boolean;
      onPress?: () => void;
    }[];
  };

  /** Linear progress (0–1) shown as a thin bar at the top of the card */
  progressPercent?: number;

  // Content
  children: React.ReactNode;

  // Actions
  onSave: () => void;
  saveLabel?: string;
  saveDisabled?: boolean;
  hideSaveButton?: boolean;
  secondaryAction?: {
    label: string;
    onPress: () => void;
    accessibilityLabel?: string;
    disabled?: boolean;
  };
  uploadedFiles?: {
    id: string;
    name: string;
  }[];
};

/**
 * Question layout component — glass card design matching the plan builder.
 */
export function QuestionLayout({
  title,
  subtitle,
  // Keep the question header consistent across every Medical History flow.
  // Individual screens should only override this for a deliberate exception.
  headerAlignment = "center",
  headerBottomSpacing = 32,
  sectionLabel,
  helpText,
  helpAccessibilityLabel,
  onBack,
  progress,
  progressPercent,
  children,
  onSave,
  saveLabel,
  saveDisabled = false,
  hideSaveButton = false,
  secondaryAction,
  uploadedFiles = [],
}: QuestionLayoutProps) {
  const navigation = useNavigation();
  // The nested stack's route name is a stable, untranslated screen id — the
  // one thing on a question screen that survives a copy edit, so it is what
  // the funnel's `step` is built from.
  const step = useRoute().name;
  const t = useTranslations();
  const capturePress = usePressCapture("QuestionLayout");
  const questionMetadata = useMedicalHistoryQuestionMetadata();
  const stepViewed = questionMetadata?.stepViewed;
  const stepSubmitted = questionMetadata?.stepSubmitted;

  useEffect(() => {
    stepViewed?.(step);
  }, [stepViewed, step]);

  const handleSave = () => {
    // Submitted, not completed: the answer still has to reach the API, and
    // the section is only finished at its completion screen.
    stepSubmitted?.(step);
    onSave();
  };
  const nextLabel = t("med_history.shared.next");
  const resolvedSaveLabel =
    saveLabel && saveLabel.trim() !== nextLabel.trim()
      ? saveLabel
      : t("med_history.shared.continue");
  const resolvedSectionLabel = sectionLabel ?? questionMetadata?.sectionLabel;
  const resolvedHelpText = helpText ?? questionMetadata?.helpText;
  const resolvedHelpAccessibilityLabel =
    helpAccessibilityLabel ??
    t("med_history.question_screen.help_accessibility_label");
  const [isHelpExpanded, setIsHelpExpanded] = useState(false);

  const handleClose = () => {
    // Go back to the parent navigator (CareNavigator) to exit the flow
    const parent = navigation.getParent();
    if (parent?.canGoBack()) {
      parent.goBack();
    } else if (navigation.canGoBack()) {
      navigation.goBack();
    }
  };

  return (
    <GlassScreenLayout>
      <View
        style={{
          flex: 1,
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : "height"}
          style={{ flex: 1 }}
        >
          {/* Glass card */}
          <View
            style={{
              ...GLASS_CARD_STYLE,
              flex: 1,
              marginBottom: 16,
              overflow: "hidden",
              padding: 0,
            }}
          >
            {/* Progress bar at the very top of the card */}
            {progressPercent != null && (
              <View
                accessible
                accessibilityRole="progressbar"
                accessibilityValue={{
                  min: 0,
                  max: 100,
                  now: Math.round(Math.min(progressPercent * 100, 100)),
                }}
                style={{
                  height: 5,
                  backgroundColor: "rgba(84, 47, 39, 0.10)",
                }}
              >
                {progressPercent > 0 && (
                  <LinearGradient
                    colors={METALLIC_PROGRESS_COLORS}
                    locations={[0, 0.24, 0.5, 0.74, 1]}
                    start={{ x: 0, y: 0.5 }}
                    end={{ x: 1, y: 0.5 }}
                    style={{
                      height: "100%",
                      width: `${Math.min(progressPercent * 100, 100)}%`,
                      borderRadius: 3,
                    }}
                  />
                )}
              </View>
            )}

            {/* Navigation row: back + context/help + close */}
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                paddingHorizontal: 16,
                paddingTop: 16,
                paddingBottom: 4,
              }}
            >
              {onBack ? (
                <Pressable
                  accessibilityLabel={t("med_history.overview.back")}
                  accessibilityRole="button"
                  onPress={() => {
                    // Chevron-only control; its accessibility label is the copy.
                    capturePress(
                      pressLabel(t("med_history.overview.back")),
                      "med_history.overview.back",
                    );
                    onBack?.();
                  }}
                  hitSlop={12}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 16,
                    backgroundColor: `${EMBER.colors.textFaint}15`,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ChevronLeft size={18} color={EMBER.colors.text} />
                </Pressable>
              ) : (
                <View style={{ width: 32 }} />
              )}
              <View
                style={{
                  alignItems: "center",
                  flex: 1,
                  flexDirection: "row",
                  gap: 8,
                  justifyContent: "center",
                  paddingHorizontal: 12,
                }}
              >
                {resolvedSectionLabel && (
                  <View
                    style={{
                      backgroundColor: `${EMBER.colors.textFaint}15`,
                      borderColor: `${EMBER.colors.textFaint}15`,
                      borderRadius: 999,
                      borderWidth: 1,
                      maxWidth: 180,
                      paddingHorizontal: 10,
                      paddingVertical: 6,
                    }}
                  >
                    <Text
                      className="font-dm-sans-medium"
                      numberOfLines={1}
                      style={{
                        color: EMBER.colors.textMid,
                        fontSize: 11,
                        fontWeight: "600",
                        lineHeight: 14,
                      }}
                    >
                      {resolvedSectionLabel}
                    </Text>
                  </View>
                )}
                {resolvedHelpText && (
                  <Pressable
                    accessibilityLabel={resolvedHelpAccessibilityLabel}
                    accessibilityRole="button"
                    accessibilityState={{ expanded: isHelpExpanded }}
                    hitSlop={10}
                    onPress={() => {
                      capturePress(
                        pressLabel(resolvedHelpAccessibilityLabel),
                        isHelpExpanded ? "collapse_help" : "expand_help",
                      );
                      setIsHelpExpanded((expanded) => !expanded);
                    }}
                    style={{
                      alignItems: "center",
                      backgroundColor: isHelpExpanded
                        ? `${EMBER.colors.warm}18`
                        : `${EMBER.colors.textFaint}12`,
                      borderRadius: 16,
                      height: 32,
                      justifyContent: "center",
                      width: 32,
                    }}
                  >
                    <Info
                      color={
                        isHelpExpanded
                          ? EMBER.colors.warm
                          : EMBER.colors.textMid
                      }
                      size={17}
                    />
                  </Pressable>
                )}
              </View>
              <Pressable
                onPress={() => {
                  // X-icon with no visible copy to name it.
                  capturePress("close_med_history_question");
                  handleClose();
                }}
                hitSlop={12}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  backgroundColor: `${EMBER.colors.textFaint}15`,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <X size={16} color={EMBER.colors.text} />
              </Pressable>
            </View>

            <ScrollView
              contentContainerStyle={{
                padding: 24,
                paddingTop: 12,
                paddingBottom: 40,
              }}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
            >
              {/* Section Stepper */}
              {progress && (
                <View
                  style={{
                    flexDirection: "row",
                    gap: 6,
                    marginBottom: 24,
                  }}
                >
                  {progress.sections.map((section, i) => (
                    <Pressable
                      key={i}
                      onPress={() => {
                        capturePress(
                          pressLabel(section.label),
                          "med_history_section_tab",
                        );
                        section.onPress?.();
                      }}
                      disabled={!section.onPress}
                      style={{
                        flex: 1,
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 4,
                        paddingVertical: 8,
                        paddingHorizontal: 8,
                        borderRadius: 20,
                        minHeight: 36,
                        backgroundColor: section.isActive
                          ? `${EMBER.colors.warm}15`
                          : `${EMBER.colors.textFaint}10`,
                        ...(section.isActive && {
                          borderWidth: 1.5,
                          borderColor: EMBER.colors.warm,
                        }),
                      }}
                    >
                      <Text
                        style={{
                          fontSize: 12,
                          fontWeight: section.isActive ? "600" : "500",
                          color: section.isActive
                            ? EMBER.colors.text
                            : EMBER.colors.textFaint,
                        }}
                        numberOfLines={1}
                      >
                        {section.label}
                      </Text>
                    </Pressable>
                  ))}
                </View>
              )}

              {/* Title */}
              <View style={{ marginBottom: headerBottomSpacing }}>
                <View
                  style={{
                    alignItems:
                      headerAlignment === "center" ? "center" : "flex-start",
                    flexDirection: "row",
                    gap: 12,
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      color: EMBER.colors.text,
                      flex: 1,
                      fontSize: 22,
                      fontWeight: "700",
                      lineHeight: 28,
                      textAlign: headerAlignment,
                    }}
                  >
                    {title}
                  </Text>
                </View>
                {subtitle && (
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 15,
                      fontWeight: "400",
                      color: EMBER.colors.textLight,
                      lineHeight: 22,
                      marginTop: 6,
                      textAlign: headerAlignment,
                    }}
                  >
                    {subtitle}
                  </Text>
                )}
                {resolvedHelpText && isHelpExpanded && (
                  <View
                    style={{
                      alignItems: "flex-start",
                      flexDirection: "row",
                      gap: 8,
                      marginTop: 12,
                    }}
                  >
                    <Info
                      color={EMBER.colors.textFaint}
                      size={15}
                      style={{ marginTop: 3 }}
                    />
                    <Text
                      className="font-dm-sans"
                      style={{
                        color: EMBER.colors.textMid,
                        flex: 1,
                        fontSize: 13,
                        lineHeight: 19,
                      }}
                    >
                      {resolvedHelpText}
                    </Text>
                  </View>
                )}
              </View>

              {/* Question Content */}
              {children}

              {uploadedFiles.length > 0 && (
                <View style={{ gap: 8, marginTop: 24, width: "100%" }}>
                  <Text
                    className="font-dm-sans-medium"
                    style={{
                      color: EMBER.colors.textMid,
                      fontSize: 13,
                      fontWeight: "600",
                    }}
                  >
                    {t("med_history.question_screen.files_added")}
                  </Text>
                  {uploadedFiles.map((file) => (
                    <DocumentRow
                      key={file.id}
                      leading={<DocumentFileTypeIcon fileName={file.name} />}
                      title={file.name}
                      subtitle={t("med_history.question_screen.file_ready")}
                      trailing={
                        <View
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: 12,
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: `${EMBER.colors.textFaint}15`,
                          }}
                        >
                          <Check
                            size={14}
                            color={EMBER.colors.textMid}
                            strokeWidth={2.5}
                          />
                        </View>
                      }
                      showChevron={false}
                    />
                  ))}
                </View>
              )}

              {!hideSaveButton && (
                <VelvetPrimaryButton
                  label={resolvedSaveLabel}
                  onPress={handleSave}
                  disabled={saveDisabled}
                  trailing={
                    <ArrowRight size={18} color="#FFFFFF" strokeWidth={2} />
                  }
                  style={{
                    marginTop: 24,
                    borderRadius: 14,
                  }}
                />
              )}

              {secondaryAction && (
                <SoftSecondaryButton
                  label={secondaryAction.label}
                  onPress={secondaryAction.onPress}
                  accessibilityLabel={
                    secondaryAction.accessibilityLabel ?? secondaryAction.label
                  }
                  disabled={secondaryAction.disabled}
                  leading={<Upload color={EMBER.colors.textMid} size={17} />}
                  style={{
                    marginTop: 12,
                    minHeight: 48,
                    borderRadius: 14,
                  }}
                />
              )}
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </View>
    </GlassScreenLayout>
  );
}
